import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api.ts';
import { ArrowLeftIcon, HomeModernIcon, UserIcon, PhoneIcon, EnvelopeIcon, CalendarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { applyCurrencyMask, parseCurrency, formatCurrencyInput } from '../utils/currency.ts';
import { applyPhoneMask } from '../utils/phone.ts';

interface RentalFormData {
  tenantName: string;
  tenantEmail?: string;
  tenantPhone?: string;
  monthlyRent: number;
  paymentDay?: number | null;
  startDate: string;
  endDate?: string | null;
  status: 'active' | 'terminated';
  details?: any;
}

interface Rental extends RentalFormData {
  id: string;
  assetId: number;
}

const EditRental: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: rental, isLoading } = useQuery<Rental>(
    ['rental', id],
    async () => {
      const response = await api.get(`/rentals/${id}`);
      return response.data;
    },
    { enabled: !!id }
  );

  const [form, setForm] = useState<RentalFormData>({
    tenantName: '',
    tenantEmail: '',
    tenantPhone: '',
    monthlyRent: 0,
    paymentDay: null,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'active',
    details: { notes: '' },
  });
  const [monthlyRentDisplay, setMonthlyRentDisplay] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (rental) {
      setForm({
        tenantName: rental.tenantName || '',
        tenantEmail: rental.tenantEmail || '',
        tenantPhone: rental.tenantPhone || '',
        monthlyRent: rental.monthlyRent || 0,
        paymentDay: rental.paymentDay ?? null,
        startDate: rental.startDate || new Date().toISOString().split('T')[0],
        endDate: rental.endDate || '',
        status: rental.status,
        details: { notes: rental.details?.notes || '' },
      });
      setMonthlyRentDisplay(formatCurrencyInput(rental.monthlyRent || 0));
    }
  }, [rental]);

  const updateMutation = useMutation(
    async (payload: RentalFormData) => {
      const response = await api.patch(`/rentals/${id}`, payload);
      return response.data;
    },
    {
      onSuccess: (updated: any) => {
        queryClient.invalidateQueries(['rental', id]);
        queryClient.invalidateQueries('rentals');
        queryClient.invalidateQueries('rentalsDashboard');
        queryClient.invalidateQueries('dashboardData');
        const assetId = updated?.assetId ?? rental?.assetId;
        navigate(assetId ? `/assets/${assetId}` : '/rentals');
      },
      onError: (error: any) => {
        console.error('Erro ao atualizar aluguel:', error);
      },
    }
  );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.tenantName) e.tenantName = 'Informe o inquilino';
    if (!monthlyRentDisplay && !Number(form.monthlyRent || 0)) e.monthlyRent = 'Informe o aluguel mensal';
    if (!form.startDate) e.startDate = 'Informe a data de início';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'notes') {
      setForm(prev => ({ ...prev, details: { ...(prev.details || {}), notes: value } }));
    } else {
      const nextValue = name === 'tenantPhone' ? applyPhoneMask(value) : value;
      setForm(prev => ({ ...prev, [name]: nextValue } as any));
    }
  };

  const handleCurrencyChange = (raw: string) => {
    const masked = applyCurrencyMask(raw);
    setMonthlyRentDisplay(masked);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const today = new Date().toISOString().split('T')[0];
    const payload: RentalFormData = {
      ...form,
      monthlyRent: monthlyRentDisplay ? parseCurrency(monthlyRentDisplay) : Number(form.monthlyRent || 0),
      paymentDay: form.paymentDay ? Number(form.paymentDay) : null,
      endDate: form.status === 'terminated' ? (form.endDate || today) : (form.endDate ? form.endDate : null),
      details: { ...(form.details || {}), notes: form.details?.notes || '' },
    };
    updateMutation.mutate(payload);
  };

  if (isLoading || !rental) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(`/assets/${rental.assetId}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar
          </button>

          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <HomeModernIcon className="h-8 w-8 mr-3 text-primary-600" />
            Editar Aluguel
          </h1>
          <p className="text-gray-600 mt-2">Imóvel #{rental.assetId}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <UserIcon className="h-6 w-6 mr-2 text-primary-600" />
              Dados do Inquilino
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inquilino *</label>
                <input
                  type="text"
                  name="tenantName"
                  value={form.tenantName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.tenantName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Nome do inquilino"
                />
                {errors.tenantName && (
                  <p className="text-red-500 text-sm mt-1">{errors.tenantName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="tenantPhone"
                    value={form.tenantPhone || ''}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="tenantEmail"
                    value={form.tenantEmail || ''}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
              Dados do Contrato
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aluguel Mensal *</label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="monthlyRent"
                    value={monthlyRentDisplay}
                    onChange={e => handleCurrencyChange(e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.monthlyRent ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="R$ 0,00"
                  />
                </div>
                {errors.monthlyRent && (
                  <p className="text-red-500 text-sm mt-1">{errors.monthlyRent}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dia de Pagamento</label>
                <input
                  type="number"
                  name="paymentDay"
                  value={form.paymentDay ?? ''}
                  onChange={handleChange}
                  min={1}
                  max={31}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="1 a 31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Início *</label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    name="startDate"
                    value={form.startDate}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.startDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.startDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Término</label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    name="endDate"
                    value={form.endDate || ''}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="active">Ativo</option>
                  <option value="terminated">Encerrado</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                <textarea
                  name="notes"
                  value={form.details?.notes || ''}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Informações adicionais do contrato"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
            >
              Salvar Alterações
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRental;
